import {
  AddressType,
  EmailType,
  PhoneNumberType,
  User,
  UserAddress,
  UserEmail,
  UserPhoneNumber,
} from "@/api/SolarCloudApi";
import { v4 as uuid } from "uuid";

function pickPhoneNumberByPriority(
  numbers: UserPhoneNumber[] | undefined
): UserPhoneNumber | undefined {
  if (!numbers || numbers.length === 0) {
    return undefined;
  }

  const reversed = [...numbers].reverse();
  const priority = [
    PhoneNumberType.DEFAULT,
    PhoneNumberType.MOBILE,
    PhoneNumberType.BUSINESS,
  ];

  for (const type of priority) {
    const match = reversed.find((phone) => phone.type === type);
    if (match) {
      return match;
    }
  }

  return reversed[0];
}

export function findDefaultPhoneNumber(
  user: Partial<User> | undefined
): UserPhoneNumber | undefined {
  return pickPhoneNumberByPriority(user?.phoneNumbers) ?? {};
}

export function updatePhoneNumbers(
  user: Partial<User> | undefined,
  phoneNumber: UserPhoneNumber | undefined
): UserPhoneNumber[] | undefined {
  if (!phoneNumber) {
    return user?.phoneNumbers;
  }

  if (!user) {
    return [phoneNumber];
  }

  if (phoneNumber.uuid) {
    for (var index = 0; index < (user.phoneNumbers?.length ?? 0); ++index) {
      if (user.phoneNumbers?.[index].uuid === phoneNumber.uuid) {
        const phoneNumbers = [...user.phoneNumbers];
        phoneNumbers[index] = phoneNumber;
        return phoneNumbers;
      }
    }
  }

  return [...(user.phoneNumbers ?? []), phoneNumber];
}

function pickAddressByPriority(
  addresses: UserAddress[] | undefined
): UserAddress | undefined {
  if (!addresses || addresses.length === 0) {
    return undefined;
  }

  const reversed = [...addresses];
  const priority = [AddressType.POSTAL,AddressType.HOME, AddressType.BUSINESS];

  for (const type of priority) {
    const match = reversed.find((address) => address.type === type);
    if (match) {
      return match;
    }
  }

  return reversed[0];
}

export function findDefaultAddress(
  user: Partial<User> | undefined
): UserAddress | undefined {
  return pickAddressByPriority(user?.addresses) ?? {};
}

export function updateAddresses(
  user: Partial<User> | undefined,
  address: UserAddress | undefined
): UserAddress[] | undefined {
  if (!address) {
    return user?.addresses;
  }

  if (!user) {
    return [address];
  }

  const existingAddresses = user.addresses ?? [];

  if (address.uuid) {
    for (var index = 0; index < existingAddresses.length; ++index) {
      if (existingAddresses[index].uuid === address.uuid) {
        const addresses = [...existingAddresses];
        addresses[index] = address;
        return addresses;
      }
    }
  } else {
    for (let index = existingAddresses.length - 1; index >= 0; --index) {
      if (existingAddresses[index].type === address.type) {
        const addresses = [...existingAddresses];
        address.uuid = existingAddresses[index].uuid;
        address.type = existingAddresses[index].type;
        addresses[index] = address;
        return addresses;
      }
    }

    const fallbackAddress = pickAddressByPriority(existingAddresses);
    if (fallbackAddress?.uuid) {
      for (let index = existingAddresses.length - 1; index >= 0; --index) {
        if (existingAddresses[index].uuid === fallbackAddress.uuid) {
          const addresses = [...existingAddresses];
          address.uuid = fallbackAddress.uuid;
          address.type = fallbackAddress.type;
          addresses[index] = address;
          return addresses;
        }
      }
    }

    address.uuid = uuid();
  }

  return [...existingAddresses, address];
}

export function findDefaultEmail(
  user: Partial<User> | undefined
): UserEmail | undefined {
  if (user === undefined) {
    return undefined;
  }

  let defaultEmail: UserEmail | undefined;

  if (user.emails) {
    for (const email of user.emails) {
      if (!defaultEmail) {
        defaultEmail = email;
      }

      switch (email.type) {
        case EmailType.PERSONAL:
          defaultEmail = email;
          break;
        case EmailType.DEFAULT:
          if (defaultEmail.type !== EmailType.BUSINESS) {
            defaultEmail = email;
          }
          break;
        default:
          break;
      }
    }
  }

  return defaultEmail ? defaultEmail : {};
}

export function updateEmails(
  user: Partial<User> | undefined,
  email: UserEmail | undefined
): UserEmail[] | undefined {
  if (!email) {
    return user?.emails;
  }

  if (!user) {
    return [email];
  }

  if (email.uuid) {
    for (let index = 0; index < (user.emails?.length ?? 0); ++index) {
      if (user.emails?.[index].uuid === email.uuid) {
        const emails = [...user.emails];
        emails[index] = email;
        return emails;
      }
    }
  } else {
    email.uuid = uuid();
  }

  return [...(user.emails ?? []), email];
}
